<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dropdown from 'primevue/dropdown'
import FileUpload from 'primevue/fileupload'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import { getAllBrands } from '@/modules/admin/brands/api/brands.service'
import { useAppToast } from '@/shared/lib/useAppToast'
import {
  applyProductImageImport,
  confirmProductImageImport,
  createProductImageImport,
  getProductImageImport,
  getProductImageImportEntries,
  preflightProductImageImport,
  updateProductImageImportEntry,
  uploadProductImageArchive
} from '../api/productImageImports.service'
import ProductImageImportEntryDialog from '../components/ProductImageImportEntryDialog.vue'
import { validateImageZipSize } from '../lib/productImageArchiveLimits'
import {
  actionLabels,
  diagnosticLabel,
  diagnosticMessage,
  matchTypeLabels,
  statusLabel,
  statusSeverity
} from '../lib/productImageImportDisplay'

const route = useRoute()
const router = useRouter()
const toast = useAppToast()
const brands = ref([])
const job = ref(null)
const entries = ref([])
const selectedBrandId = ref(null)
const selectedEntry = ref(null)
const filter = ref('all')
const page = ref(1)
const pageSize = 50
const hasNextPage = ref(false)
const preflight = ref(null)
const applyResult = ref(null)
const selectedFile = ref(null)
const loading = ref(false)
const creating = ref(false)
const uploading = ref(false)
const mutating = ref(false)
const dialogVisible = ref(false)
const confirmVisible = ref(false)
const applyVisible = ref(false)

const jobId = computed(() => route.params.jobId ? String(route.params.jobId) : null)
const returnImportJobId = computed(() => route.query.returnImportJobId ? String(route.query.returnImportJobId) : null)
const brandOptions = computed(() => brands.value.map(brand => ({ label: brand.name, value: brand.id })))
const status = computed(() => job.value?.status || 'Created')
const isReady = computed(() => status.value === 'ReadyForReview')
const isConfirmed = computed(() => status.value === 'Confirmed')
const isApplying = computed(() => status.value === 'Applying')
const isApplied = computed(() => status.value === 'Applied')
const canEdit = computed(() => isReady.value && !mutating.value)
const canReview = computed(() => !!job.value && !['Created', 'Processing'].includes(status.value))
const filteredEntries = computed(() => {
  if (filter.value === 'attention') return entries.value.filter(entry => entry.action === 'Conflict' || ['Unmatched', 'Ambiguous', 'Duplicate', 'InvalidFile', 'InvalidImage'].includes(entry.status))
  if (filter.value === 'ready') return entries.value.filter(entry => entry.status === 'Ready' && entry.action !== 'Skip')
  if (filter.value === 'skipped') return entries.value.filter(entry => entry.action === 'Skip')
  if (filter.value === 'unmatched') return entries.value.filter(entry => entry.status === 'Unmatched')
  if (filter.value === 'ambiguous') return entries.value.filter(entry => entry.status === 'Ambiguous')
  if (filter.value === 'conflict') return entries.value.filter(entry => entry.action === 'Conflict')
  return entries.value
})
const filterOptions = [
  { label: 'Все на странице', value: 'all' },
  { label: 'Требуют внимания', value: 'attention' },
  { label: 'Готово', value: 'ready' },
  { label: 'Не найдено', value: 'unmatched' },
  { label: 'Неоднозначно', value: 'ambiguous' },
  { label: 'Конфликты', value: 'conflict' },
  { label: 'Пропущено', value: 'skipped' }
]

function isMutationError(error) {
  return error?.raw?.error === 'IMAGE_IMPORT.REVISION_CONFLICT'
}

async function recoverFromError(error) {
  toast.error(diagnosticMessage(error))
  if (isMutationError(error)) await reload()
}

async function loadBrands() {
  try {
    brands.value = await getAllBrands()
    const requested = Number(route.query.brandId)
    selectedBrandId.value = Number.isInteger(requested) && brands.value.some(brand => Number(brand.id) === requested) ? requested : null
  } catch (error) { toast.error(diagnosticMessage(error)) }
}

function returnToExcelImport() {
  if (returnImportJobId.value) router.push({ name: 'adminImportWorkspace', params: { jobId: returnImportJobId.value } })
}

async function loadEntries() {
  if (!jobId.value || !canReview.value) return
  try {
    const result = await getProductImageImportEntries(jobId.value, page.value, pageSize)
    entries.value = Array.isArray(result) ? result : result.items || []
    hasNextPage.value = entries.value.length === pageSize
  } catch (error) { await recoverFromError(error) }
}

async function reload() {
  if (!jobId.value) return
  loading.value = true
  try {
    job.value = await getProductImageImport(jobId.value)
    applyResult.value = job.value.applyResult || null
    await loadEntries()
  } catch (error) {
    await recoverFromError(error)
    if (error?.status === 404) await router.replace({ name: 'adminProductImageImports' })
  } finally { loading.value = false }
}

async function createJob() {
  if (!selectedBrandId.value) return
  creating.value = true
  try {
    const created = await createProductImageImport({ brandId: selectedBrandId.value })
    await router.push({ name: 'adminProductImageImport', params: { jobId: created.id } })
  } catch (error) { await recoverFromError(error) } finally { creating.value = false }
}

function selectFile(event) {
  const file = event.files?.[0]
  selectedFile.value = null
  if (file) {
    try { validateImageZipSize(file) } catch (error) { toast.warn(error.message); return }
  }
  selectedFile.value = file?.name?.toLowerCase().endsWith('.zip') ? file : null
  if (file && !selectedFile.value) toast.warn('Выберите ZIP-архив.')
}

async function upload() {
  if (!selectedFile.value || !jobId.value) return
  uploading.value = true
  try {
    await uploadProductImageArchive(jobId.value, selectedFile.value)
    selectedFile.value = null
    toast.success('Архив загружен и проанализирован.')
    page.value = 1
    await reload()
  } catch (error) { await recoverFromError(error) } finally { uploading.value = false }
}

function openEntry(entry) {
  if (!canEdit.value) return
  selectedEntry.value = entry
  dialogVisible.value = true
}

async function saveEntry(payload) {
  if (!selectedEntry.value || !job.value) return
  mutating.value = true
  try {
    await updateProductImageImportEntry(jobId.value, selectedEntry.value.id, {
      expectedRevision: job.value.revision,
      action: payload.action,
      productTypeId: payload.productTypeId,
      productId: payload.productId
    })
    dialogVisible.value = false
    await reload()
  } catch (error) { await recoverFromError(error) } finally { mutating.value = false }
}

async function runPreflight() {
  if (!job.value) return
  mutating.value = true
  try {
    preflight.value = await preflightProductImageImport(jobId.value, job.value.revision)
    if (!preflight.value.canConfirm) toast.warn('План требует исправлений.')
    else toast.success('План готов к подтверждению.')
  } catch (error) { await recoverFromError(error) } finally { mutating.value = false }
}

async function confirmPlan() {
  if (!job.value) return
  mutating.value = true
  try {
    const result = await confirmProductImageImport(jobId.value, job.value.revision)
    confirmVisible.value = false
    toast.success('План импорта подтверждён.')
    await reload()
    job.value = { ...job.value, confirmationId: result.confirmationId, revision: result.revision, status: result.status }
  } catch (error) { await recoverFromError(error) } finally { mutating.value = false }
}

async function applyPlan() {
  if (!job.value) return
  const confirmationId = job.value.confirmationId || applyResult.value?.confirmationId
  if (!confirmationId) { toast.error('Не найден идентификатор подтверждения.') ; return }
  mutating.value = true
  try {
    const result = await applyProductImageImport(jobId.value, { expectedRevision: job.value.revision, confirmationId })
    applyVisible.value = false
    applyResult.value = result
    toast.success(result.status === 'CleanupPending' ? 'Изображения применены, очистка старых файлов ожидает повторной попытки.' : 'Импорт изображений завершён.')
    await reload()
  } catch (error) { await recoverFromError(error) } finally { mutating.value = false }
}

async function changePage(nextPage) {
  page.value = nextPage
  await loadEntries()
}

function countLabel(count) { return count ?? 0 }

onMounted(async () => {
  if (jobId.value) await reload()
  else await loadBrands()
})

watch(jobId, async (currentJobId, previousJobId) => {
  if (currentJobId === previousJobId) return
  page.value = 1
  preflight.value = null
  applyResult.value = null
  if (currentJobId) await reload()
  else {
    job.value = null
    entries.value = []
    await loadBrands()
  }
})
watch(() => route.query.brandId, () => {
  if (!jobId.value && brands.value.length) {
    const requested = Number(route.query.brandId)
    selectedBrandId.value = Number.isInteger(requested) && brands.value.some(brand => Number(brand.id) === requested) ? requested : null
  }
})
</script>

<template>
  <main class="image-import-page">
    <div class="page-shell">
      <header class="page-header">
        <div>
          <Button label="Администрирование" icon="pi pi-arrow-left" text @click="router.push('/admin')" />
          <Button v-if="returnImportJobId" label="Вернуться к Excel import" icon="pi pi-arrow-left" text @click="returnToExcelImport" />
          <h1>Импорт изображений</h1>
          <p>ZIP → сопоставление по Article/ModelCode → проверка → применение</p>
        </div>
        <Tag v-if="job" :value="status" :severity="isApplied ? 'success' : isApplying ? 'info' : 'secondary'" />
      </header>

      <Card v-if="!job" class="setup-card">
        <template #title>1. Настройка импорта</template>
        <template #content>
          <div class="setup-grid">
            <label>Бренд *<Dropdown v-model="selectedBrandId" :options="brandOptions" option-label="label" option-value="value" filter placeholder="Выберите Brand" /></label>
            <Button label="Создать импорт" icon="pi pi-plus" :loading="creating" :disabled="!selectedBrandId" @click="createJob" />
          </div>
        </template>
      </Card>

      <template v-else>
        <Card v-if="status === 'Created'" class="step-card">
          <template #title>2. Загрузка ZIP-архива</template>
          <template #content>
            <div class="upload-row">
              <FileUpload mode="basic" accept=".zip,application/zip" choose-label="Выбрать ZIP" :auto="false" @select="selectFile" />
              <span v-if="selectedFile">{{ selectedFile.name }}</span>
              <Button label="Загрузить и проанализировать" icon="pi pi-upload" :loading="uploading" :disabled="!selectedFile" @click="upload" />
            </div>
          </template>
        </Card>

        <Card v-else class="step-card">
          <template #title>Импорт {{ job.originalFileName || job.id }}</template>
          <template #content>
            <div class="summary-grid">
              <div><strong>{{ countLabel(job.counts?.ready) }}</strong><span>Готово</span></div>
              <div><strong>{{ countLabel(job.counts?.unmatched) }}</strong><span>Не найдено</span></div>
              <div><strong>{{ countLabel(job.counts?.ambiguous) }}</strong><span>Неоднозначно</span></div>
              <div><strong>{{ countLabel(job.counts?.duplicate) }}</strong><span>Дубликаты</span></div>
              <div><strong>{{ countLabel(job.counts?.invalid) }}</strong><span>Некорректно</span></div>
            </div>
            <div v-if="isApplying" class="notice info"><i class="pi pi-spin pi-spinner"></i> Импорт применяется...</div>
            <div v-if="isApplied" class="notice success"><i class="pi pi-check-circle"></i> Каталог применён. Cleanup: {{ applyResult?.status || job.applyResult?.status || 'Applied' }}.</div>
          </template>
        </Card>

        <Card v-if="canReview" class="review-card">
          <template #title>3. Проверка и исправления</template>
          <template #content>
            <div class="review-toolbar">
              <Dropdown v-model="filter" :options="filterOptions" option-label="label" option-value="value" />
              <small>Фильтр применяется к загруженной странице ({{ page }}).</small>
              <Button label="Обновить" icon="pi pi-refresh" outlined :loading="loading" @click="reload" />
            </div>
            <DataTable :value="filteredEntries" data-key="id" striped-rows :loading="loading" responsive-layout="scroll">
              <Column field="originalFileName" header="Файл" />
              <Column header="Определено по"><template #body="{ data }">{{ matchTypeLabels[data.matchType] || data.matchType }}</template></Column>
              <Column header="ProductType"><template #body="{ data }">{{ data.productTypeModelCode || data.productTypeId || '—' }}</template></Column>
              <Column header="Product"><template #body="{ data }">{{ data.productArticle || data.productId || '—' }}</template></Column>
              <Column header="Статус"><template #body="{ data }"><Tag :value="statusLabel(data.status)" :severity="statusSeverity(data.status)" /></template></Column>
              <Column header="Текущие изображения"><template #body="{ data }">{{ data.existingImageCount > 0 ? `Есть (${data.existingImageCount})` : 'Нет' }}</template></Column>
              <Column header="Действие"><template #body="{ data }"><Tag :value="actionLabels[data.action] || data.action" :severity="data.action === 'Conflict' ? 'warn' : data.action === 'Skip' ? 'secondary' : 'success'" /></template></Column>
              <Column header=""><template #body="{ data }"><Button v-if="canEdit && (data.action === 'Conflict' || data.status !== 'Ready')" label="Исправить" icon="pi pi-pencil" text @click="openEntry(data)" /><Button v-else-if="canEdit" label="Изменить" text @click="openEntry(data)" /></template></Column>
            </DataTable>
            <div class="pager"><Button label="Назад" icon="pi pi-chevron-left" outlined :disabled="page <= 1 || loading" @click="changePage(page - 1)" /><span>Страница {{ page }}</span><Button label="Далее" icon="pi pi-chevron-right" icon-pos="right" outlined :disabled="!hasNextPage || loading" @click="changePage(page + 1)" /></div>
          </template>
        </Card>

        <Card v-if="isReady" class="actions-card">
          <template #content>
            <div class="workflow-actions"><Button label="Проверить импорт" icon="pi pi-search" :loading="mutating" @click="runPreflight" /><Button label="Подтвердить импорт" icon="pi pi-check" severity="success" :disabled="!preflight?.canConfirm || mutating" @click="confirmVisible = true" /></div>
            <div v-if="preflight && !preflight.canConfirm" class="diagnostics"><strong>План требует исправлений:</strong><div v-for="item in preflight.errors" :key="`${item.code}-${item.entryId}`">{{ diagnosticLabel(item) }}</div></div>
            <div v-else-if="preflight?.canConfirm" class="notice success">Готово к подтверждению: добавить {{ preflight.executableCount }}, пропустить {{ preflight.skippedCount }}.</div>
          </template>
        </Card>

        <Card v-if="isConfirmed || isApplied" class="apply-card">
          <template #content>
            <div v-if="isConfirmed" class="workflow-actions"><Button label="Применить импорт" icon="pi pi-cloud-upload" severity="success" :loading="mutating" @click="applyVisible = true" /></div>
            <div v-if="job.applyResult || applyResult" class="result-grid"><span>Добавлено: {{ (applyResult || job.applyResult).added }}</span><span>Пропущено: {{ (applyResult || job.applyResult).skipped }}</span><span v-if="(applyResult || job.applyResult).cleanupPending">Очистка ранее созданного импорта ожидает повторной попытки</span></div>
          </template>
        </Card>
      </template>
    </div>

    <ProductImageImportEntryDialog v-model:visible="dialogVisible" :entry="selectedEntry" :brand-id="job?.brandId" :submitting="mutating" @save="saveEntry" />
    <Dialog v-model:visible="confirmVisible" modal header="Подтвердить импорт" :style="{ width: 'min(480px, 92vw)' }"><p>После подтверждения план станет неизменяемым. Продолжить?</p><template #footer><Button label="Отмена" text @click="confirmVisible = false" /><Button label="Подтвердить" :loading="mutating" @click="confirmPlan" /></template></Dialog>
    <Dialog v-model:visible="applyVisible" modal header="Применить импорт" :style="{ width: 'min(480px, 92vw)' }"><p>Выбранные изображения будут добавлены в каталог. Существующие изображения и основное изображение товара сохранятся.</p><template #footer><Button label="Отмена" text @click="applyVisible = false" /><Button label="Применить" severity="success" :loading="mutating" @click="applyPlan" /></template></Dialog>
  </main>
</template>

<style scoped>
.image-import-page { min-height: 100vh; background: #f7f7f6; }.page-shell { max-width: 1400px; margin: 0 auto; padding: 24px; }.page-header { display: flex; justify-content: space-between; align-items: end; gap: 20px; margin-bottom: 20px; }.page-header h1 { margin: 8px 0 6px; font-size: clamp(28px, 4vw, 42px); letter-spacing: -.04em; }.page-header p { margin: 0; color: #777; }.setup-card, .step-card, .review-card, .actions-card, .apply-card { margin-bottom: 16px; }.setup-grid, .upload-row, .review-toolbar, .workflow-actions, .pager, .result-grid { display: flex; align-items: center; gap: 12px; }.setup-grid label { display: grid; gap: 7px; min-width: 280px; font-weight: 600; font-size: 13px; }.summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }.summary-grid div { display: grid; gap: 4px; padding: 14px; border: 1px solid #e6e6e3; border-radius: 10px; }.summary-grid strong { font-size: 24px; }.summary-grid span, .review-toolbar small { color: #777; font-size: 12px; }.review-toolbar { margin-bottom: 14px; }.review-toolbar small { margin-right: auto; }.pager { justify-content: center; padding-top: 16px; }.notice, .diagnostics { margin-top: 14px; padding: 12px 14px; border-radius: 9px; }.notice.success { background: #e8f6ec; color: #216b3a; }.notice.info { background: #e8f0fb; color: #26547e; }.diagnostics { background: #fff5e5; color: #795018; display: grid; gap: 5px; }.result-grid { flex-wrap: wrap; font-weight: 600; }.result-grid span { padding: 10px 12px; background: #f3f3f0; border-radius: 8px; }
@media (max-width: 800px) { .page-shell { padding: 14px 10px 35px; }.page-header, .setup-grid, .upload-row, .review-toolbar { align-items: stretch; flex-direction: column; }.setup-grid label { min-width: 0; width: 100%; }.summary-grid { grid-template-columns: repeat(2, 1fr); }.review-toolbar small { margin-right: 0; } }
</style>
